import React, { useCallback, useEffect, useState } from 'react';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';
import {
  Button,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import {
  Close as CloseIcon,
  MoreVert as MoreVertIcon
} from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import ConfirmDialog from '../../common/dialogs/ConfirmDialog';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import AppSnackbar from '../../common/AppSnackbar';
import SearchBox from '../../common/SearchBox';
import StatusLabel from '../../common/StatusLabel';
import { roles } from '../../user/User.interface';
import RoleSaveDialog from './dialogs/RoleSaveDialog';
import { useDeleteRole, useFindRoles } from './Role.hooks';
import { Role } from './Role.interface';

const RoleListPage: React.FC = () => {
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [type, setType] = useQueryParam('type', StringParam);
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<FetchError>();
  const [updateRole, setUpdateRole] = useState<Role>();
  const [removeRole, setRemoveRole] = useState<Role>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [alertMessage, setAlertMessage] = useState<{
    open: boolean;
    message?: React.ReactNode;
  }>({ open: false });
  const {
    roleList,
    isLoading: isFinding,
    error: fetchError,
    get
  } = useFindRoles(false);
  const {
    role: deletedRole,
    isLoading: isDeleting,
    error: deleteError,
    del
  } = useDeleteRole(removeRole?.id || 0);
  const isLoading = isFinding || isDeleting;

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  const findRolesWithParams = useCallback(() => {
    const params = {
      page,
      limit,
      filter: search,
      type: type || '',
      order: String(order || '')
    };
    if (order && direction) {
      params.order = `${order} ${direction}`;
    }
    get(params);
  }, [page, limit, order, direction, search, type, get]);

  useEffect(() => {
    findRolesWithParams();
  }, [findRolesWithParams]);

  useEffect(() => {
    setError(fetchError || deleteError);
  }, [fetchError, deleteError]);

  useEffect(() => {
    if (deletedRole) {
      setAlertMessage({ open: true, message: 'Role removed successfully' });
      findRolesWithParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletedRole]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Roles</h2>
        <SearchBox value={search} onSearch={value => setSearch(value)} />
        <PageTemplateHeaderActions>
          <Button
            color='primary'
            variant='contained'
            onClick={() => setOpen(true)}
          >
            Add Role
          </Button>
          <IconButton
            size='small'
            color='secondary'
            onClick={event => setAnchorEl(event.currentTarget)}
          >
            <MoreVertIcon />
          </IconButton>
          {anchorEl && (
            <Popover
              open
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <List>
                <ListItem
                  button
                  onClick={() => {
                    setAnchorEl(null);
                    setType(type => (type === 'custom' ? 'default' : 'custom'));
                  }}
                >
                  <ListItemText
                    primary={`${
                      type === 'custom' ? 'Default' : 'Custom'
                    } Roles Only`}
                  />
                </ListItem>
                {type && (
                  <ListItem
                    button
                    onClick={() => {
                      setAnchorEl(null);
                      setType(undefined);
                    }}
                  >
                    <ListItemText primary='All Roles (No Type Filter)' />
                  </ListItem>
                )}
              </List>
            </Popover>
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateCardOver>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'name'}
                    direction={getSortDirection('name')}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Type</TableCell>
                <TableCell width={1} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={3} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {roleList?.data.map(role => {
                const roleItem = roles.find(r => r.name === role.name);
                const isCustom = !roleItem;
                return (
                  <TableRow
                    key={role.id}
                    hover={isCustom}
                    onClick={() => {
                      if (isCustom) {
                        setUpdateRole(role);
                        setOpen(true);
                      }
                    }}
                  >
                    <TableCell>{roleItem?.label || role.name}</TableCell>
                    <TableCell>
                      <StatusLabel
                        color={isCustom ? 'to-do' : 'draft'}
                        status={isCustom ? 'Custom' : 'Default'}
                      />
                    </TableCell>
                    <TableCell>
                      {isCustom && (
                        <Button
                          color='primary'
                          variant='outlined'
                          endIcon={<CloseIcon />}
                          onClick={event => {
                            event.stopPropagation();
                            setRemoveRole(role);
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component='div'
          count={roleList?.total || 0}
          rowsPerPage={limit}
          page={page}
          onPageChange={(_, page) => setPage(page)}
          onRowsPerPageChange={event => {
            setLimit(+event.target.value);
            setPage(0);
          }}
        />
      </PageTemplateCardOver>

      {open && (
        <RoleSaveDialog
          open={open}
          role={updateRole}
          onCancel={() => {
            setOpen(false);
            setUpdateRole(undefined);
          }}
          onSave={(_, created) => {
            setOpen(false);
            setUpdateRole(undefined);
            setAlertMessage({
              open: true,
              message: `Role ${created ? 'created' : 'updated'} successfully`
            });
            findRolesWithParams();
          }}
        />
      )}
      <ConfirmDialog
        open={!!removeRole}
        title='Remove Role'
        message={`Are you sure you want to remove the role "${
          removeRole?.name || '?'
        }"?`}
        onCancel={() => setRemoveRole(undefined)}
        onConfirm={() => {
          setRemoveRole(undefined);
          del();
        }}
      />

      <AppSnackbar
        severity='success'
        open={alertMessage.open}
        onClose={() => {
          setAlertMessage(message => ({ ...message, open: false }));
        }}
      >
        {alertMessage.message}
      </AppSnackbar>
      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message || 'An error occurred.'}
      </AppSnackbar>
    </PageTemplate>
  );
};

export default RoleListPage;
